import i18n from '@/locales'
import type { AdminViewerState } from '@/helpers/adminSnapshotState'
import { samePersistedValue } from '@/helpers/adminSnapshotState'
import {
  cloneTransform,
  createQuaternion,
  createVector3,
  getAutodeskViewing,
  getThree,
  invalidateViewer,
  type ExplodeExtension,
  type StoredFragmentTransform,
  type ToolbarButton,
  type TransformControl,
  type TransformMesh,
  type Vector3,
  type ViewerInstance,
  type ViewerModel,
  type ViewerTool
} from '@/helpers/autodeskViewer'

type Options = {
  getViewer: () => ViewerInstance | null
  getLockedNodeIds: () => string[]
  onPartLocationChange: () => void
}

export const translateToolbarControlId = 'translate-tool'
const translateToolName = 'Viewing.Translate.Tool'
const transformOverlayName = 'TransformToolOverlay'

export default function useAutodeskViewerPartLocations ({ getViewer, getLockedNodeIds, onPartLocationChange }: Options) {
  const initialFragmentTransforms = new Map<number, StoredFragmentTransform>()
  const defaultFragmentTransforms = new Map<number, StoredFragmentTransform>()
  const partLocationTransforms = new Map<number, StoredFragmentTransform>()
  const selectedFragmentTransforms = new Map<number, { offset: Vector3, transform: StoredFragmentTransform }>()
  let isTranslateToolActive = false
  let isTranslating = false
  let hasActiveTransformChange = false
  let transformControl: TransformControl | null = null
  let transformMesh: TransformMesh | null = null

  function getFragmentTransform (fragId: number): StoredFragmentTransform | null {
    const THREE = getThree()
    const fragmentList = getViewer()?.model?.getFragmentList?.()

    if (!THREE || !fragmentList) return null

    // Unmoved fragments leave these output values unchanged.
    const scale = new THREE.Vector3(1, 1, 1)
    const rotation = new THREE.Quaternion()
    const translation = new THREE.Vector3()
    fragmentList.getAnimTransform(fragId, scale, rotation, translation)

    return {
      rotation: { w: rotation.w, x: rotation.x, y: rotation.y, z: rotation.z },
      scale: { x: scale.x, y: scale.y, z: scale.z },
      translation: { x: translation.x, y: translation.y, z: translation.z }
    }
  }

  function updateFragmentTransform (fragId: number, transform: StoredFragmentTransform) {
    const fragmentList = getViewer()?.model?.getFragmentList?.()
    if (!fragmentList) return

    fragmentList.updateAnimTransform(
      fragId,
      createVector3(transform.scale),
      createQuaternion(transform.rotation),
      createVector3(transform.translation)
    )
  }

  function getSelectedFragmentIds () {
    const viewer = getViewer()
    const selectedDbIds = viewer?.getSelection?.() ?? []
    const instanceTree = viewer?.model?.getData?.().instanceTree
    const lockedNodeIds = getLockedNodeIds()
    const fragmentIds: number[] = []

    if (!instanceTree) return fragmentIds

    selectedDbIds.forEach((dbId) => {
      let parentId = instanceTree.getNodeParentId?.(dbId)
      const visited = new Set<number>()
      while (parentId !== undefined && parentId !== dbId && !visited.has(parentId)) {
        if (lockedNodeIds.includes(String(parentId))) return
        visited.add(parentId)
        parentId = instanceTree.getNodeParentId?.(parentId)
      }
      instanceTree.enumNodeFragments(dbId, (fragId) => fragmentIds.push(fragId), true)
    })

    return fragmentIds
  }

  function setTranslateButtonState (active: boolean) {
    const buttonState = getAutodeskViewing()?.UI?.ButtonState
    const navTools = getViewer()?.getToolbar?.()?.getControl?.('navTools')
    const translateControl = navTools?.getControl?.(translateToolbarControlId) as ToolbarButton | null

    if (!translateControl) return

    translateControl.removeClass?.(active ? 'inactive' : 'active')
    translateControl.removeClass?.(active ? 'translate-tool' : 'translate-tool-active')
    translateControl.addClass?.(active ? 'active' : 'inactive')
    translateControl.addClass?.(active ? 'translate-tool-active' : 'translate-tool')

    if (!buttonState || !translateControl.setState) return

    translateControl.setState(active ? buttonState.ACTIVE : buttonState.INACTIVE)
  }

  function setTranslateToolActive (active: boolean) {
    const viewer = getViewer()
    if (!viewer?.toolController) return
    if (active && !ensureTransformControl()) return

    isTranslateToolActive = active
    if (active) {
      viewer.toolController.activateTool(translateToolName)
    } else {
      viewer.toolController.deactivateTool(translateToolName)
    }
    setTranslateButtonState(active)
  }

  function createTransformMesh () {
    const THREE = getThree()
    const materialManager = getViewer()?.impl?.matman?.()

    if (!THREE) return null

    const material = new THREE.MeshPhongMaterial({ color: 0xff0000 })
    materialManager?.addMaterial?.('transform-tool-material', material, true)

    return new THREE.Mesh(new THREE.SphereGeometry(0.0001, 5, 5), material)
  }

  function getModelRadius () {
    const boundingBox = (getViewer()?.model as ViewerModel & {
      getBoundingBox?: () => {
        getBoundingSphere?: () => {
          radius?: number
        }
      }
    } | undefined)?.getBoundingBox?.()

    return boundingBox?.getBoundingSphere?.().radius ?? 1
  }

  function ensureTransformControl () {
    const viewer = getViewer()
    const THREE = getThree()
    const TransformControls = getAutodeskViewing()?.Private?.TransformControls
    const camera = viewer?.impl?.camera
    const canvas = viewer?.impl?.canvas

    if (!viewer?.impl || !THREE || !camera || !canvas || !TransformControls) return false
    if (transformControl && transformMesh) return true

    viewer.impl.createOverlayScene?.(transformOverlayName)
    transformControl = new TransformControls(camera, canvas, 'translate')
    transformControl.setSize?.(getModelRadius() * 5)
    transformControl.visible = false
    transformControl.addEventListener('change', handleTransformChange)
    viewer.impl.addOverlay?.(transformOverlayName, transformControl)

    transformMesh = createTransformMesh()
    if (!transformMesh) {
      disposeTransformControl()
      return false
    }

    transformControl.attach(transformMesh)

    return true
  }

  function setTransformControlPosition (position: Vector3) {
    if (!transformControl || !transformMesh) return

    transformMesh.position.x = position.x
    transformMesh.position.y = position.y
    transformMesh.position.z = position.z
    transformControl.setPosition?.(position)
    transformControl.visible = true
    transformControl.update?.()
    invalidateViewer(getViewer())
  }

  function updateTranslateSelection () {
    const viewer = getViewer()
    if ((viewer?.getExplodeScale?.() ?? 0) > 0) clearExplodeState()
    const THREE = getThree()
    const fragments = viewer?.model?.getFragmentList?.()
    const fragmentIds = getSelectedFragmentIds()
    if (!THREE || !fragments || fragmentIds.length === 0) {
      clearTransformSelection()
      return
    }

    const bounds = new THREE.Box3()
    const fragmentBounds = new THREE.Box3()
    fragmentIds.forEach((fragId) => {
      fragments.getWorldBounds(fragId, fragmentBounds)
      bounds.union(fragmentBounds)
    })
    if (bounds.isEmpty()) {
      clearTransformSelection()
      return
    }
    initializeTransformSelection(bounds.getCenter(new THREE.Vector3()))
  }

  function clearTransformSelection () {
    selectedFragmentTransforms.clear()
    hasActiveTransformChange = false
    isTranslating = false

    if (transformControl) {
      transformControl.visible = false
    }
    invalidateViewer(getViewer())
  }

  function disposeTransformControl () {
    const viewer = getViewer()
    if (transformControl) {
      transformControl.removeEventListener('change', handleTransformChange)
      viewer?.impl?.removeOverlay?.(transformOverlayName, transformControl)
      transformControl.dispose?.()
    }

    viewer?.impl?.removeOverlayScene?.(transformOverlayName)
    transformControl = null
    transformMesh = null
    selectedFragmentTransforms.clear()
    hasActiveTransformChange = false
    isTranslating = false
  }

  function initializeTransformSelection (hitPoint: Vector3) {
    const fragmentIds = getSelectedFragmentIds()

    if (fragmentIds.length === 0 || !ensureTransformControl()) return false

    selectedFragmentTransforms.clear()
    fragmentIds.forEach((fragId) => {
      const transform = getFragmentTransform(fragId)
      if (!transform) return

      if (!initialFragmentTransforms.has(fragId)) {
        initialFragmentTransforms.set(fragId, cloneTransform(defaultFragmentTransforms.get(fragId) ?? transform))
      }

      selectedFragmentTransforms.set(fragId, {
        offset: {
          x: hitPoint.x - transform.translation.x,
          y: hitPoint.y - transform.translation.y,
          z: hitPoint.z - transform.translation.z
        },
        transform: cloneTransform(transform)
      })
    })

    if (selectedFragmentTransforms.size === 0) return false

    setTransformControlPosition(hitPoint)

    return true
  }

  function handleTransformChange () {
    if (!isTranslating || !transformControl?.visible || !transformMesh) {
      invalidateViewer(getViewer())
      return
    }

    const controlPosition = transformMesh.position
    selectedFragmentTransforms.forEach(({ offset, transform }, fragId) => {
      const nextTransform = {
        ...cloneTransform(transform),
        translation: {
          x: controlPosition.x - offset.x,
          y: controlPosition.y - offset.y,
          z: controlPosition.z - offset.z
        }
      }
      updateFragmentTransform(fragId, nextTransform)
      partLocationTransforms.set(fragId, cloneTransform(nextTransform))
    })

    hasActiveTransformChange = selectedFragmentTransforms.size > 0
    invalidateViewer(getViewer())
  }

  const translateTool: ViewerTool = {
    activate: () => {
      isTranslateToolActive = ensureTransformControl()
      if (isTranslateToolActive) {
        // Translate must read assembled transforms, never temporary explode offsets.
        clearExplodeState()
        updateTranslateSelection()
      }
      setTranslateButtonState(isTranslateToolActive)
    },
    deactivate: () => {
      isTranslateToolActive = false
      disposeTransformControl()
      setTranslateButtonState(false)
    },
    getName: () => translateToolName,
    getNames: () => [translateToolName],
    handleButtonDown: (event, button) => {
      if (button !== 0 || !transformControl?.visible) return false

      isTranslating = true
      hasActiveTransformChange = false
      const handled = transformControl.onPointerDown(event)
      isTranslating = handled
      // The SDK starts dragging even on a miss; release it so selection can continue.
      if (!handled) transformControl.onPointerUp(event)
      return handled
    },
    handleButtonUp: (event) => {
      if (!isTranslating) return false

      const handled = event ? transformControl?.onPointerUp(event) ?? false : false
      isTranslating = false

      if (hasActiveTransformChange) {
        onPartLocationChange()
      }

      return handled || hasActiveTransformChange
    },
    handleMouseMove: (event) => {
      if (!transformControl?.visible) return false
      if (isTranslating) return transformControl?.onPointerMove(event) ?? false

      return transformControl?.onPointerHover(event) ?? false
    }
  }

  function addTranslateToolbarControl () {
    const Button = getAutodeskViewing()?.UI?.Button
    const navTools = getViewer()?.getToolbar?.()?.getControl?.('navTools')

    if (!Button || !navTools?.addControl || navTools.getControl?.(translateToolbarControlId)) return

    const translateButton = new Button(translateToolbarControlId)
    translateButton.setToolTip?.(i18n.global.t('threeD.translate'))
    translateButton.setIcon?.('translate-tool-icon')
    translateButton.addClass?.('inactive')
    translateButton.addClass?.('translate-tool')
    translateButton.onClick = () => {
      setTranslateToolActive(!isTranslateToolActive)
    }

    navTools.addControl(translateButton)
  }

  function updateTransformControl () {
    transformControl?.update?.()
  }

  function handleSelectionChange () {
    if (isTranslateToolActive && !isTranslating) updateTranslateSelection()
  }

  function reapplyPartLocations () {
    partLocationTransforms.forEach((transform, fragId) => updateFragmentTransform(fragId, transform))
    if (isTranslateToolActive) updateTranslateSelection()
  }

  function captureDefaultTransforms () {
    const fragmentCount = getViewer()?.model?.getFragmentList?.().getCount?.() ?? 0
    for (let fragId = 0; fragId < fragmentCount; fragId++) {
      const transform = getFragmentTransform(fragId)
      if (transform) defaultFragmentTransforms.set(fragId, cloneTransform(transform))
    }
  }

  function clearExplodeState () {
    const viewer = getViewer()
    const explode = viewer?.getExtension?.('Autodesk.Explode') as ExplodeExtension | undefined
    explode?.deactivate()
    viewer?.explode?.(0)
    defaultFragmentTransforms.forEach((transform, fragId) => updateFragmentTransform(fragId, transform))
    partLocationTransforms.forEach((transform, fragId) => updateFragmentTransform(fragId, transform))
  }

  function getPartLocations () {
    const partLocations: AdminViewerState['partLocations'] = {}
    partLocationTransforms.forEach((transform, fragId) => {
      if (!samePersistedValue(transform, initialFragmentTransforms.get(fragId))) {
        partLocations[String(fragId)] = cloneTransform(transform)
      }
    })
    return partLocations
  }

  function restorePartLocations (partLocations: AdminViewerState['partLocations']) {
    resetPartLocations()
    Object.entries(partLocations).forEach(([id, transform]) => {
      const fragId = Number(id)
      const original = defaultFragmentTransforms.get(fragId) ?? getFragmentTransform(fragId)
      if (original) initialFragmentTransforms.set(fragId, cloneTransform(original))
      partLocationTransforms.set(fragId, cloneTransform(transform))
      updateFragmentTransform(fragId, transform)
    })
  }

  function resetPartLocations () {
    const hasChangedPartLocations = initialFragmentTransforms.size > 0

    initialFragmentTransforms.forEach((transform, fragId) => {
      updateFragmentTransform(fragId, cloneTransform(transform))
    })
    initialFragmentTransforms.clear()
    partLocationTransforms.clear()
    clearTransformSelection()
    invalidateViewer(getViewer())

    if (hasChangedPartLocations) {
      onPartLocationChange()
    }
  }

  return {
    translateTool,
    addTranslateToolbarControl,
    setTranslateToolActive,
    updateTransformControl,
    handleSelectionChange,
    reapplyPartLocations,
    captureDefaultTransforms,
    clearExplodeState,
    getPartLocations,
    restorePartLocations,
    resetPartLocations,
    disposeTransformControl
  }
}
